import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../services/api";

export default function BodyCompositionPage() {
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // History toggle state
  const [historyExpanded, setHistoryExpanded] = useState(true);

  // Form & Editing states
  const [editingId, setEditingId] = useState(null);
  const [fatPercentage, setFatPercentage] = useState("");
  const [muscleValue, setMuscleValue] = useState("");
  const [muscleUnit, setMuscleUnit] = useState("kg");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");

  // Goal states
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [goalFat, setGoalFat] = useState("20");
  const [freqDays, setFreqDays] = useState(7);
  const [savingGoal, setSavingGoal] = useState(false);
  const [goalFeedback, setGoalFeedback] = useState("");

  const fetchData = async () => {
    try {
      const [lRes, hRes, pRes] = await Promise.all([
        api.get("/readings/body-composition/latest").catch(() => null),
        api.get("/readings/body-composition/history").catch(() => null),
        api.get("/users/profile").catch(() => null),
      ]);
      if (lRes?.data?.latest) setLatest(lRes.data.latest);
      if (hRes?.data?.items) setHistory(hRes.data.items);
      if (pRes?.data?.profile) setProfile(pRes.data.profile);
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setFatPercentage(item.fatPercentage ? String(item.fatPercentage).replace(".", ",") : "");
    setMuscleValue(item.muscleValue ? String(item.muscleValue).replace(".", ",") : "");
    setMuscleUnit(item.muscleUnit || "kg");
    const dStr = item.measuredAt ? String(item.measuredAt).split("T")[0] : new Date().toISOString().split("T")[0];
    setDate(dStr);
    setNotes(item.notes || "");
    setFeedback("");
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFatPercentage("");
    setMuscleValue("");
    setMuscleUnit("kg");
    setDate(new Date().toISOString().split("T")[0]);
    setNotes("");
    setFeedback("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!fatPercentage && !muscleValue) return;
    setSaving(true);
    setFeedback("");

    const payload = {
      fatPercentage: fatPercentage ? Number(String(fatPercentage).replace(",", ".")) : undefined,
      muscleValue: muscleValue ? Number(String(muscleValue).replace(",", ".")) : undefined,
      muscleUnit,
      measuredAt: date,
      notes,
    };

    try {
      if (editingId) {
        await api.put(`/readings/body-composition/${editingId}`, payload);
        setFeedback("Composição corporal atualizada com sucesso! ✨");
      } else {
        await api.post("/readings/body-composition", payload);
        setFeedback("Composição corporal salva com sucesso! ✨");
      }
      handleCancelEdit();
      await fetchData();
    } catch {
      setFeedback("Erro ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente remover este registro?")) return;
    try {
      await api.delete(`/readings/body-composition/${id}`);
      await fetchData();
    } catch {
      alert("Não foi possível excluir o registro.");
    }
  };

  const handleStartEditGoal = () => {
    setIsEditingGoal(true);
    setGoalFeedback("");
  };

  const handleSaveGoal = async (e) => {
    e.preventDefault();
    setSavingGoal(true);
    setGoalFeedback("");
    try {
      setGoalFeedback("Meta atualizada com sucesso! 🎯");
      setIsEditingGoal(false);
    } catch {
      setGoalFeedback("Erro ao atualizar a meta.");
    } finally {
      setSavingGoal(false);
    }
  };

  const freqLabel = freqDays === 1 ? "1 dia (Diário)" : `${freqDays} dias`;

  return (
    <div className="space-y-4 pb-24 font-sans">
      {/* 1. BOTÃO VOLTAR PARA A HOME */}
      <div className="flex items-center justify-between pb-1">
        <Link
          to="/app/home"
          className="inline-flex items-center gap-1.5 text-xs font-extrabold text-[#6C54D8] hover:text-[#5A45BF] bg-purple-100/80 hover:bg-purple-200/80 px-3.5 py-2 rounded-2xl border border-purple-200/60 transition-all cursor-pointer shadow-2xs"
        >
          <span className="text-sm">←</span>
          <span>Voltar</span>
        </Link>
      </div>

      {/* 2. SITUAÇÃO ATUAL */}
      <div className="glass-card rounded-3xl p-5 border border-purple-100/70 space-y-2.5">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#6D6680] block">
              Composição Corporal Atual
            </span>
            <h2 className="text-2xl font-black text-[#24152F] font-serif mt-0.5">
              Gordura: <span className="text-purple-700">{latest?.fatPercentage ? `${latest.fatPercentage}%` : "—"}</span>
            </h2>
          </div>

          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-purple-100/80 text-[#6C54D8] border border-purple-200/50 text-2xl">
            🧘
          </div>
        </div>

        <div className="space-y-1 pt-1 border-t border-purple-100/60">
          <p className="text-xs font-semibold text-purple-900">
            {latest?.measuredAt ? (
              <>Data do registro · {new Date(latest.measuredAt).toLocaleDateString("pt-BR")}</>
            ) : (
              "Sem registros recentes"
            )}
          </p>
          {latest?.muscleValue && (
            <p className="text-xs font-medium text-gray-600">
              Massa Muscular: <strong className="font-bold text-[#24152F]">{latest.muscleValue} {latest.muscleUnit}</strong>
            </p>
          )}
        </div>
      </div>

      {/* 3. FORMULÁRIO DE REGISTRO */}
      <form onSubmit={handleSave} className="glass-card rounded-3xl p-5 border border-purple-100/70 space-y-3.5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#24152F]">
            {editingId ? "✏️ Editar Composição" : "Registrar Composição"}
          </h3>
          {editingId && (
            <button
              type="button"
              onClick={handleCancelEdit}
              className="text-xs font-bold text-gray-500 hover:text-gray-700 cursor-pointer underline"
            >
              Cancelar
            </button>
          )}
        </div>

        {feedback && (
          <div className="p-3 rounded-2xl bg-purple-50 border border-purple-200 text-xs font-bold text-purple-800">
            {feedback}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
              Gordura Corporal (%)
            </label>
            <input
              type="text"
              placeholder="Ex: 22,5"
              value={fatPercentage}
              onChange={(e) => setFatPercentage(e.target.value)}
              className="input-base h-11 w-full text-sm font-bold box-border"
            />
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
              Massa Muscular ({muscleUnit})
            </label>
            <input
              type="text"
              placeholder="Ex: 32,0"
              value={muscleValue}
              onChange={(e) => setMuscleValue(e.target.value)}
              className="input-base h-11 w-full text-sm font-bold box-border"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
              Data
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input-base h-11 w-full text-sm font-bold box-border"
              required
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block">
                Observação (máx. 50 caracteres)
              </label>
              <span className="text-[10px] text-gray-400 font-semibold">{notes.length}/50</span>
            </div>
            <input
              type="text"
              maxLength={50}
              placeholder="Ex: Medido por bioimpedância de manhã"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input-base h-11 w-full text-sm box-border"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full h-12 rounded-2xl bg-[#6C54D8] hover:bg-[#5A45BF] active:scale-95 text-white text-xs font-black uppercase tracking-wider shadow-md transition-all cursor-pointer disabled:opacity-60"
        >
          {saving ? "Salvando..." : editingId ? "Atualizar Composição" : "Salvar Composição"}
        </button>
      </form>

      {/* 4. META E PERIODICIDADE */}
      <div className="glass-card rounded-3xl p-5 border border-purple-100/70 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">🎯</span>
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-[#24152F]">
              Meta de Composição Corporal
            </h3>
          </div>

          {!isEditingGoal && (
            <button
              type="button"
              onClick={handleStartEditGoal}
              className="text-xs font-bold text-[#6C54D8] hover:text-[#5A45BF] cursor-pointer px-3 py-1 rounded-xl bg-purple-50 hover:bg-purple-100 transition-all border border-purple-200/60"
            >
              Editar meta
            </button>
          )}
        </div>

        {goalFeedback && (
          <div className="p-2.5 rounded-xl bg-purple-50 border border-purple-200 text-xs font-bold text-purple-800">
            {goalFeedback}
          </div>
        )}

        {isEditingGoal ? (
          <form onSubmit={handleSaveGoal} className="p-4 rounded-2xl bg-purple-50/80 border border-purple-200 space-y-3">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
                Meta de Gordura (%)
              </label>
              <input
                type="text"
                value={goalFat}
                onChange={(e) => setGoalFat(e.target.value)}
                className="input-base h-11 w-full text-sm font-bold box-border"
              />
            </div>

            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
                Registrar composição a cada
              </label>
              <select
                value={freqDays}
                onChange={(e) => setFreqDays(Number(e.target.value))}
                className="input-base h-11 w-full text-sm font-bold box-border bg-white cursor-pointer"
              >
                <option value={1}>1 dia (Diário)</option>
                <option value={3}>3 dias</option>
                <option value={7}>7 dias (1 semana)</option>
                <option value={14}>14 dias (2 semanas)</option>
                <option value={30}>30 dias (1 mês)</option>
              </select>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <button
                type="submit"
                disabled={savingGoal}
                className="h-11 px-5 rounded-2xl bg-[#6C54D8] hover:bg-[#5A45BF] text-white text-xs font-black uppercase tracking-wider shadow-md transition-all cursor-pointer disabled:opacity-60"
              >
                {savingGoal ? "Salvando..." : "Salvar Meta"}
              </button>
              <button
                type="button"
                onClick={() => setIsEditingGoal(false)}
                className="h-11 px-4 rounded-2xl bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-bold transition-all cursor-pointer"
              >
                Cancelar
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-[#24152F]">
              Meta de Gordura: <strong className="font-extrabold">{goalFat}%</strong>
            </p>
            <p className="text-xs font-medium text-purple-800">
              Acompanhamento: <strong className="font-bold text-[#24152F]">A cada {freqLabel}</strong>
            </p>
          </div>
        )}
      </div>

      {/* 5. HISTÓRICO PADRONIZADO */}
      <div className="glass-card rounded-3xl p-5 border border-purple-100/70 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-extrabold uppercase tracking-wider text-[#6D6680]">
            Histórico de Composição Corporal
          </h3>
          <button
            type="button"
            onClick={() => setHistoryExpanded(!historyExpanded)}
            className="text-xs font-bold text-[#6C54D8] hover:text-[#5A45BF] cursor-pointer flex items-center gap-1"
          >
            {historyExpanded ? "Ocultar histórico" : `Exibir Histórico (${history.length})`}
          </button>
        </div>

        {historyExpanded && (
          <div className="space-y-3 pt-1">
            {history.length > 0 ? (
              history.map((item) => (
                <div
                  key={item.id}
                  className="p-3.5 rounded-2xl border border-purple-100/80 bg-white/90 space-y-1.5 transition-all hover:border-purple-200"
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2.5">
                      <span className="text-xs font-semibold text-gray-500">
                        {item.measuredAt ? new Date(item.measuredAt).toLocaleDateString("pt-BR") : "—"}
                      </span>
                      <span className="text-base font-black text-[#24152F]">
                        {item.fatPercentage ? `${item.fatPercentage}% gordura` : ""} {item.muscleValue ? `• ${item.muscleValue} ${item.muscleUnit}` : ""}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-xs font-bold">
                      <button
                        type="button"
                        onClick={() => handleStartEdit(item)}
                        className="text-[#6C54D8] hover:text-[#5A45BF] cursor-pointer"
                      >
                        Editar
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        className="text-red-500 hover:text-red-700 cursor-pointer"
                      >
                        Excluir
                      </button>
                    </div>
                  </div>

                  {item.notes && (
                    <p className="text-xs text-gray-600 font-medium line-clamp-2 pt-0.5 border-t border-gray-100 mt-1">
                      {item.notes}
                    </p>
                  )}
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-500 font-medium py-2">
                Nenhum registro no histórico.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
